/**
 * Client-side row filtering: position, team, rookies-only and name search, the tier rows that
 * stay visible for the current filter, and the league-calculated tier dividers.
 *
 * Calls on sibling mixins via `this`: Sorting (sortColumn, sortSections, allSections,
 * refreshTable), GuestCta (repositionGuestCta), MobileTableChrome (mobileStickyTierLabel),
 * UserLeague (cachedLeagueData, selectedUserLeagueId), UrlAndScoring (updateUrl,
 * appendPersistentParams), CoreState (basePath).
 */
import { getFantasyPositionsForRosterSpot } from './../../helpers/MvpBoardScoringHelper.js';
import { OFFENSIVE_FLEX_ROSTER_SPOTS } from './../../helpers/RosterSpotHelper.js';

export default () => ({
    playerSearchTerm: '',
    selectedTeam: '',
    selectedTeamLabel: '',
    selectedFantasyPosition: '',
    positionFilterOpen: false,
    teamFilterOpen: false,
    visibleTiers: [],

    // Normalized search term cache, see updateSearchVisibility
    _lastSearchRaw: null,
    _searchTermLowerCase: null,

    initFiltering() {
        this.$watch('playerSearchTerm', () => this.updateSearchVisibility());
        this.$watch('selectedFantasyPosition', () => this.refreshTable());
        this.$watch('selectedTeam', () => this.refreshTable());
        this.$watch('rookiesOnly', () => this.refreshTable());
    },

    handleFantasyPositionChange(value) {
        this.selectedFantasyPosition = value;
        this.updateUrl();
        // Client-side filtering already updated the table; hit load-table only for OOB
        // title / meta / H1 / main-copy fragments (swap:none skips table replace).
        const form = document.querySelector('#sharedParams');
        if (!form || typeof htmx === 'undefined') {
            return;
        }
        const values = Object.fromEntries(new FormData(form));
        // Alpine may not have flushed :value onto the hidden input yet.
        values.fantasyPosition = value;
        htmx.ajax('GET', this.appendPersistentParams(`/${this.basePath}/load-table`), {
            target: '#rankingsTableContainer',
            swap: 'none',
            values,
        });
    },

    updateVisiblePlayerRanks() {
        const table = this.$refs.rankingsTable;
        const playerRows = Array.from(table.querySelectorAll('tbody[data-player-row]')).filter(tbody => tbody.offsetParent !== null);

        playerRows.forEach((tbody, i) => {
            const rankSpan = tbody.querySelector('.rank-index');
            if (rankSpan) rankSpan.textContent = i + 1;
        });
    },

    computeVisibleTiers(frag = null) {
        const visibleTiers = new Set();
        const table = frag || document;
        // Find all visible player rows and collect their tiers
        table.querySelectorAll('[data-player-row]').forEach(row => {
            if (this.isVisiblePlayerRow(row) && row.classList.contains('hidden-row') === false) {
                const tierInfo = this.getPlayerTierInfo(row);
                if (tierInfo) {
                    visibleTiers.add(tierInfo);
                }
            }
        });
        this.removeDuplicateTiers(table);
        this.visibleTiers = Array.from(visibleTiers);
    },

    removeDuplicateTiers(frag) {
        const seen = new Set();
        frag.querySelectorAll('[data-tier-row]').forEach(row => {
            const key = row.dataset.tierKey;
            if (seen.has(key)) {
                row.remove();
            } else {
                seen.add(key);
            }
        });
    },

    // FLEX is RB/WR/TE. Position tiers are separate scales, so FLEX uses overall tier like All Positions.
    usesOverallTier() {
        const position = (this.selectedFantasyPosition || '').toUpperCase();
        return position === '' || position === 'FLEX';
    },

    // Tier number on player tbody (data-tier-overall / data-tier-positional).
    // Used by getPlayerTierInfo and league divider rebuild.
    getPlayerRowTier(row) {
        return this.usesOverallTier() ? row.dataset.tierOverall : row.dataset.tierPositional;
    },

    // Returns visibleTiers key (e.g. "overall-3"). Reads player row dataset - works for
    // default PHP-rendered tiers and Use My League WASM updates (replaces old DOM walk).
    getPlayerTierInfo(el) {
        if (!this.sortColumn.includes('dsValue')) {
            return null;
        }
        const tier = this.getPlayerRowTier(el);
        if (!tier) {
            return null;
        }
        return this.usesOverallTier() ? `overall-${tier}` : `${this.selectedFantasyPosition}-${tier}`;
    },

    // Clone #rankingsTierRowTemplate and set attrs for one league-calculated tier divider.
    createLeagueTierRow(tierNumber) {
        const templateRoot = document.getElementById('rankingsTierRowTemplate')?.content.firstElementChild;
        if (!templateRoot) {
            return null;
        }

        const tierRow = templateRoot.cloneNode(true);
        const isOverall = this.usesOverallTier();
        tierRow.dataset.tierNumber = tierNumber;
        tierRow.dataset.tierKey = isOverall ? `overall-${tierNumber}` : `${this.selectedFantasyPosition}-${tierNumber}`;
        tierRow.dataset.tierType = isOverall ? 'overall' : 'positional';
        if (isOverall) {
            delete tierRow.dataset.fantasyPosition;
        } else {
            tierRow.dataset.fantasyPosition = this.selectedFantasyPosition;
        }
        tierRow.querySelector('.sticky-left').textContent = `Tier ${tierNumber}`;
        return tierRow;
    },

    // Use My League: WASM recalculates tiers but leaves PHP divider rows stale. Walk sorted
    // player rows and inject fresh dividers when data-tier-overall/positional changes.
    appendLeagueSortedSections(frag, sorted, keepAttached, parked) {
        let lastTier = null;

        sorted.forEach(section => {
            const row = section.el;
            if (!row.hasAttribute('data-player-row')) {
                return;
            }

            const tier = this.getPlayerRowTier(row);
            const isVisiblePlayerRow =
                row.style.display !== 'none' && !row.classList.contains('hidden-row') && this.isVisiblePlayerRow(row);

            if (isVisiblePlayerRow && tier && tier !== lastTier) {
                const tierRow = this.createLeagueTierRow(tier);
                if (tierRow) {
                    frag.appendChild(tierRow);
                    lastTier = tier;
                }
            }

            if (keepAttached(section)) {
                frag.appendChild(row);
            } else {
                row.remove();
                parked.push(row);
            }
        });
    },

    isVisibleRow(el) {
        // Handle tier rows
        if (el.hasAttribute('data-tier-row')) {
            return this.isTierRowVisible(el);
        }
        // Handle player rows
        return this.isVisiblePlayerRow(el);
    },

    getFirstVisibleTier() {
        // Find the first visible tier row based on the current view mode
        // If no tiers are visible, return an empty string
        const firstVisibleTier = Array.from(this.$refs.rankingsTable.querySelectorAll('tbody[data-tier-row]')).find(tierRow => {
            return this.isTierRowVisible(tierRow);
        });
        return firstVisibleTier ? firstVisibleTier.dataset.tierNumber || '' : '';
    },

    isTierRowVisible(tierRow) {
        if (!this.sortColumn.includes('dsValue')) {
            // Only show tiers if sorting by dsValue
            return false;
        }
        const tierData = tierRow.dataset;
        const tierType = tierData.tierType; // 'overall' or 'positional'
        const tierNumber = tierData.tierNumber;
        const position = tierData.fantasyPosition; // for positional tiers
        // Only show tiers that match current view mode. FLEX uses overall tiers.
        if (this.usesOverallTier() && tierType !== 'overall') {
            return false;
        }
        if (!this.usesOverallTier() && (tierType !== 'positional' || position !== this.selectedFantasyPosition)) {
            return false;
        }
        // Check if this tier has any visible players
        const tierKey = this.usesOverallTier() ? `overall-${tierNumber}` : `${position}-${tierNumber}`;
        return this.visibleTiers.includes(tierKey);
    },

    isVisiblePlayerRow(el) {
        let isVisible = true;

        const selectedFantasyPosition = this.selectedFantasyPosition;
        if (selectedFantasyPosition && selectedFantasyPosition !== '') {
            // Check if the row's fantasy position matches the selected one
            isVisible = el.dataset.fantasyPosition === selectedFantasyPosition;
            if (selectedFantasyPosition.toUpperCase() === 'IDP') {
                isVisible = ['DL', 'LB', 'DB'].includes(el.dataset.fantasyPosition);
            }
            if (selectedFantasyPosition.toUpperCase() === 'FLEX') {
                isVisible = ['RB', 'WR', 'TE'].includes(el.dataset.fantasyPosition);
            }

            // --- User leagues can select specific flex roster spots (not jusf FLEX)
            const selectedUserLeague = this.cachedLeagueData[this.selectedUserLeagueId]?.leagueData?.mvpBoardScoring;
            if (selectedUserLeague && OFFENSIVE_FLEX_ROSTER_SPOTS.includes(selectedFantasyPosition)) {
                const fantasyPositionsForRosterSpot = getFantasyPositionsForRosterSpot(selectedFantasyPosition, selectedUserLeague);
                isVisible = fantasyPositionsForRosterSpot.includes(el.dataset.fantasyPosition);
            }
        } else if (this.selectedFantasyPosition === '' && this.basePath === 'dynasty-rankings') {
            // For dynasty rankings, if no position is selected we filter out K, DEF, and IDP
            isVisible = !['K', 'DEF', 'DL', 'LB', 'DB'].includes(el.dataset.fantasyPosition);
        } else if (this.selectedFantasyPosition === '' && ['rankings', 'ros-rankings'].includes(this.basePath)) {
            // For preseason and ROS, if no position is selected, filter out IDP positions (DL, LB, DB)
            if (['DL', 'LB', 'DB'].includes(el.dataset.fantasyPosition)) {
                isVisible = false;
            }
        }

        if (this.selectedTeam && this.selectedTeam !== '') {
            // Check if the row's team matches the selected one
            isVisible = isVisible && el.dataset.teamId === this.selectedTeam;
        }

        if (this.rookiesOnly) {
            // Check if the row is a rookie
            isVisible = isVisible && el.dataset.isRookie === 'true';
        }

        return isVisible;
    },

    updateSearchVisibility() {
        this.mobileStickyTierLabel?.classList.remove('search-hidden');
        this.allSections.forEach(row => {
            row.el.classList.remove('search-hidden');
        });

        // Early return for short/empty search
        if (!this.playerSearchTerm || this.playerSearchTerm.length < 3) {
            // Re-attach any parked rows the cleared search filter had hidden
            this.sortSections();
            this.repositionGuestCta();
            return;
        }

        // Cache normalized search term once per input change
        if (this._lastSearchRaw !== this.playerSearchTerm) {
            this._lastSearchRaw = this.playerSearchTerm;
            // Normalize search term
            this._searchTermLowerCase = (this._lastSearchRaw || '').trim().toLowerCase();
        }
        if (!this._searchTermLowerCase) {
            this.sortSections();
            this.repositionGuestCta();
            return;
        }

        // Apply search filtering
        this.allSections.forEach(row => {
            if (!row.el._playerNameLowerCase || !row.el._playerNameLowerCase.includes(this._searchTermLowerCase)) {
                row.el.classList.add('search-hidden');
            }
        });

        // The mobile sticky tier label is not relevant during search and gets in the way
        this.mobileStickyTierLabel?.classList.add('search-hidden');

        // Matches may currently be parked off-DOM (windowing) - rebuild the attached set
        this.sortSections();
        this.repositionGuestCta();
    },
});
